import React from 'react';
import moment from 'moment';
import { rgba } from 'polished';
import { graphic } from 'echarts';
import { COLOR_WHITE, COLOR_LIME_GREEN, COLOR_BLUE } from '../../../assets/styles/colors';
import chartDot from '../../../assets/img/svg/chart-dot.svg';
import chartDotBlue from '../../../assets/img/svg/chart-dot-blue.svg';
import numeral from 'numeral';
import { useIntl } from 'react-intl';

export const usePredictionChartOptions = (formattedChartData) => {
  const { formatMessage: fm } = useIntl();

  const formatter = React.useCallback(
    (params) => {
      let tooltip = '';
      params.reverse().forEach((param) => {
        let text =
          param.seriesIndex === 0
            ? fm({ id: 'investor_prediction_chart_tooltip_initial_deposit' })
            : fm({ id: 'investor_prediction_chart_tooltip_monthly_deposits' });
        let value =
          param.seriesIndex === 0 ? param.data.investments : param.data.investmentsPrediction;
        tooltip += `<div style="background:${
          param.color
        }; capitalize;box-shadow: 0 0 10px 0 rgba(12,233,100,0.4);border-top-right-radius:20px;border-bottom-right-radius:20px;font-size:12px;line-height:15px;padding:6px 20px 5px 16px;">
      ${text}</br>
      <b>${numeral(value).format('0,0.00')} € </b></br>
      </div>${params.length > 1 ? '<br/>' : ''}`;
      });

      return tooltip;
    },
    [fm]
  );

  return React.useMemo(
    () => ({
      grid: {
        left: 0,
        right: 22,
        top: 20,
        bottom: 30,
        containLabel: true,
      },
      legend: {
        bottom: 0,
        textStyle: {
          color: rgba(COLOR_WHITE, 0.4),
        },
        inactiveColor: rgba(COLOR_WHITE, 0.4),
        icon: 'circle',
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'transparent',
        textStyle: {
          fontSize: 12,
          lineHeight: 26,
        },
        formatter,
        axisPointer: {
          type: 'line',
          snap: true,
          axis: 'x',
          lineStyle: {
            color: COLOR_WHITE,
          },
          label: {
            show: true,
            backgroundColor: 'transparent',
            formatter: (value) => {
              if (value.axisDimension === 'x') {
                const fdate = moment(value.value).format('MMM YYYY');
                return fdate.charAt(0).toUpperCase() + fdate.slice(1);
              } else {
                return `${numeral(value.value).format('0,0.00')} €`;
              }
            },
          },
        },
      },
      xAxis: {
        type: 'time',
        boundaryGap: false,
        splitLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        axisLine: {
          lineStyle: {
            color: rgba(COLOR_WHITE, 0.1),
          },
        },
        axisLabel: {
          inside: false,
          color: rgba(COLOR_WHITE, 0.4),
          fontFamily: 'Source Sans Pro',
          formatter(date) {
            const fdate = moment(date).format('MMM YYYY');
            return fdate.charAt(0).toUpperCase() + fdate.slice(1);
          },
        },
      },
      yAxis: {
        type: 'value',
        min: function(value) {
          const minNum = Number(value.min);
          return minNum;
        },
        max: function(value) {
          const maxNum = Number(value.max);
          return maxNum;
        },
        splitNumber: 4,
        splitLine: {
          lineStyle: {
            color: rgba(COLOR_WHITE, 0.1),
            type: 'dashed',
          },
        },
        axisLine: {
          lineStyle: {
            color: rgba(COLOR_WHITE, 0.1),
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          inside: false,
          color: rgba(COLOR_WHITE, 0.4),
          fontFamily: 'Source Sans Pro',
          formatter(value) {
            return `${numeral(parseFloat(value)).format('0,0.00')} €`;
          },
        },
      },
      dataset: {
        source: formattedChartData,
      },
      series: [
        {
          type: 'line',
          name: fm({ id: 'investor_prediction_chart_legend_initial_deposit' }),
          symbol: `image://${chartDot}`,
          dimensions: [
            {
              type: 'time',
              name: 'date',
            },
            {
              type: 'float',
              name: 'investments',
            },
          ],
          showAllSymbol: 'false',
          symbolSize: 8,
          hoverAnimation: true,
          itemStyle: {
            color: `${COLOR_LIME_GREEN}`,
          },
          areaStyle: {
            normal: {
              color: new graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: rgba(COLOR_LIME_GREEN, 0.5) },
                { offset: 1, color: rgba(COLOR_LIME_GREEN, 0) },
              ]),
            },
          },
        },
        {
          type: 'line',
          name: fm({ id: 'investor_prediction_chart_legend_monthly_deposits' }),
          symbol: `image://${chartDotBlue}`,
          showAllSymbol: 'false',
          symbolSize: 8,
          hoverAnimation: true,
          itemStyle: {
            color: `${COLOR_BLUE}`,
          },
          dimensions: [
            {
              type: 'time',
              name: 'date',
            },
            {
              type: 'float',
              name: 'investmentsPrediction',
            },
          ],
          areaStyle: {
            normal: {
              color: new graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: rgba(COLOR_BLUE, 0.8) },
                { offset: 1, color: rgba(COLOR_BLUE, 0) },
              ]),
            },
          },
        },
      ],
    }),
    [fm, formattedChartData, formatter]
  );
};
